import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../styles/pages/CaseStudiesPage.css';

const CaseStudiesPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  // Solution showcases - what we can build for each industry
  const solutionShowcases = [
    {
      id: 1,
      title: 'Digital Banking Platform',
      category: 'fintech',
      industry: 'Financial Services',
      image: 'https://images.unsplash.com/photo-1616077168079-7e09a677fb2c?auto=format&fit=crop&w=800&q=80',
      imageAlt: 'Digital banking platform on mobile device',
      overview: 'Modern digital banking platforms with mobile-first design, real-time transactions, and AI-powered security.',
      challenge: 'Banks struggle with legacy systems that can\'t meet modern digital demands. Customers expect seamless mobile experiences and instant transactions.',
      solution: 'We build comprehensive digital banking ecosystems including mobile apps, web platforms, and API infrastructure with real-time processing and biometric authentication.',
      capabilities: [
        'Mobile-first banking applications',
        'Real-time transaction processing',
        'AI-powered fraud detection',
        'Secure biometric authentication'
      ],
      technologies: ['React Native', 'Node.js', 'MongoDB', 'AI/ML', 'Blockchain']
    },
    {
      id: 2,
      title: 'Healthcare Management System',
      category: 'healthcare',
      industry: 'Healthcare',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800&q=80',
      imageAlt: 'Healthcare management system with patient data',
      overview: 'Integrated hospital management systems connecting multiple facilities with centralized patient care and operational efficiency.',
      challenge: 'Healthcare facilities operate with disconnected systems, leading to patient data silos, inefficient resource allocation, and poor coordination.',
      solution: 'We develop cloud-based HMS with centralized patient records, real-time bed tracking, integrated pharmacy systems, and telemedicine capabilities.',
      capabilities: [
        'Centralized patient record management',
        'Real-time resource tracking',
        'Telemedicine integration',
        'HIPAA & NDPR compliance'
      ],
      technologies: ['React', 'Python', 'PostgreSQL', 'AWS', 'HIPAA Compliance']
    },
    {
      id: 3,
      title: 'E-Learning Platform',
      category: 'education',
      industry: 'Education',
      image: 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?auto=format&fit=crop&w=800&q=80',
      imageAlt: 'Online learning platform with students',
      overview: 'Comprehensive learning management systems with live classes, assessments, and collaboration tools for educational institutions.',
      challenge: 'Educational institutions need scalable online learning platforms with features for live teaching, assessment, and student engagement.',
      solution: 'We build custom LMS with live video streaming, interactive whiteboards, automated grading, plagiarism detection, and mobile learning support.',
      capabilities: [
        'Live video streaming & recording',
        'Automated grading systems',
        'Plagiarism detection',
        'Mobile learning support'
      ],
      technologies: ['Vue.js', 'WebRTC', 'Redis', 'MySQL', 'CDN']
    },
    {
      id: 4,
      title: 'Smart Farm Management',
      category: 'agriculture',
      industry: 'Agriculture',
      image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?auto=format&fit=crop&w=800&q=80',
      imageAlt: 'Smart farming with technology and crops',
      overview: 'IoT-enabled farm management systems providing real-time crop monitoring, irrigation automation, and predictive analytics.',
      challenge: 'Large-scale farms struggle with manual monitoring, leading to resource waste, delayed pest detection, and suboptimal harvest planning.',
      solution: 'We implement IoT sensors, satellite imagery integration, AI-powered pest detection, automated irrigation, and predictive harvest analytics.',
      capabilities: [
        'Real-time soil & crop monitoring',
        'Automated irrigation control',
        'AI pest detection',
        'Predictive yield analytics'
      ],
      technologies: ['IoT', 'Python', 'TensorFlow', 'React', 'AWS IoT']
    },
    {
      id: 5,
      title: 'E-Commerce Marketplace',
      category: 'ecommerce',
      industry: 'E-Commerce',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=800&q=80',
      imageAlt: 'E-commerce marketplace with products',
      overview: 'Multi-vendor e-commerce marketplaces with advanced logistics integration and multiple payment options.',
      challenge: 'Vendors need access to online markets with reliable logistics and payment solutions tailored for Nigerian customers.',
      solution: 'We build scalable marketplaces with vendor onboarding, inventory management, multiple payment gateways, and logistics API integration.',
      capabilities: [
        'Multi-vendor marketplace architecture',
        'Integrated payment gateways',
        'Logistics & tracking integration',
        'Vendor analytics dashboard'
      ],
      technologies: ['Next.js', 'Stripe', 'Paystack', 'Elasticsearch', 'Docker']
    },
    {
      id: 6,
      title: 'Real Estate Property Portal',
      category: 'realestate',
      industry: 'Real Estate',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80',
      imageAlt: 'Modern real estate property building',
      overview: 'Comprehensive property listing and management platforms with virtual tours, advanced search, and agent tools.',
      challenge: 'Property seekers struggle to find verified listings while agents lack modern tools for property showcasing.',
      solution: 'We develop feature-rich portals with 360° virtual tours, advanced search, AI recommendations, agent CRM, and verification systems.',
      capabilities: [
        '360° virtual property tours',
        'Advanced search & filters',
        'Agent CRM & tools',
        'Property verification systems'
      ],
      technologies: ['React', '3D.js', 'Mapbox', 'Node.js', 'Smart Contracts']
    }
  ];

  const categories = [
    { id: 'all', label: 'All Solutions' },
    { id: 'fintech', label: 'FinTech' },
    { id: 'healthcare', label: 'Healthcare' },
    { id: 'education', label: 'Education' },
    { id: 'agriculture', label: 'Agriculture' },
    { id: 'ecommerce', label: 'E-Commerce' },
    { id: 'realestate', label: 'Real Estate' }
  ];

  const filteredShowcases = activeFilter === 'all'
    ? solutionShowcases
    : solutionShowcases.filter(showcase => showcase.category === activeFilter);

  return (
    <div className="case-studies-page">
      <section className="case-studies-hero">
        <div className="case-studies-hero-content">
          <motion.div
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6 }}
            className="hero-badge"
          >
            Solution Showcases
          </motion.div>

          <motion.h1
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="case-studies-hero-title"
          >
            Solutions We <span className="gradient-text">Build For You</span>
          </motion.h1>

          <motion.p
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="case-studies-hero-subtitle"
          >
            Explore the types of digital solutions we design and build for businesses across diverse industries,
            tailored to solve real challenges.
          </motion.p>
        </div>
      </section>

      <section className="case-studies-content">
        <div className="case-studies-container">
          <motion.div
            initial={{ y: 15 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6 }}
            className="filter-section"
          >
            <div className="filter-buttons">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`filter-button ${activeFilter === category.id ? 'active' : ''}`}
                  onClick={() => setActiveFilter(category.id)}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </motion.div>

          <div className="case-studies-grid">
            {filteredShowcases.map((showcase, index) => (
              <motion.div
                key={showcase.id}
                initial={{ y: 15 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="case-study-card"
              >
                {/* Card Image */}
                <div className="case-study-image-wrapper">
                  <img
                    src={showcase.image}
                    alt={showcase.imageAlt}
                    loading="lazy"
                    className="case-study-card-image"
                  />
                  <div className="case-study-image-overlay" />
                </div>

                <div className="case-study-header">
                  <div className="case-study-meta">
                    <span className="case-study-industry">{showcase.industry}</span>
                  </div>
                </div>

                <h3 className="case-study-title">{showcase.title}</h3>

                <div className="case-study-section">
                  <h4>Overview</h4>
                  <p>{showcase.overview}</p>
                </div>

                <div className="case-study-section">
                  <h4>Challenge We Solve</h4>
                  <p>{showcase.challenge}</p>
                </div>

                <div className="case-study-section">
                  <h4>Our Approach</h4>
                  <p>{showcase.solution}</p>
                </div>

                <div className="case-study-section">
                  <h4>Key Capabilities</h4>
                  <ul className="results-list">
                    {showcase.capabilities.map((capability, idx) => (
                      <li key={idx}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M13.707 4.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-3-3a1 1 0 011.414-1.414L7 9.586l5.293-5.293a1 1 0 011.414 0z" fill="currentColor"/>
                        </svg>
                        {capability}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="case-study-footer">
                  <div className="technologies">
                    {showcase.technologies.slice(0, 3).map((tech, idx) => (
                      <span key={idx} className="tech-tag">{tech}</span>
                    ))}
                    {showcase.technologies.length > 3 && (
                      <span className="tech-tag more">+{showcase.technologies.length - 3}</span>
                    )}
                  </div>
                </div>

                <Link to="/contact" className="case-study-cta">
                  Discuss Your Project
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="case-studies-cta-section">
        <div className="case-studies-cta-container">
          <div className="case-studies-cta-content">
            <h2>Ready to Build Your Solution?</h2>
            <p>
              Let's collaborate to design and build innovative solutions tailored to your specific needs.
              Our team is ready to turn your vision into reality.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="primary-button">
                Start Your Project
              </Link>
              <Link to="/services" className="secondary-button">
                Explore Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
